import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Modal from "../../common/Modal";
import FileUploader from "../../common/FileUploader";
import toast from "react-hot-toast";
import { useSubmission } from "../../../hooks/useCallApi";

const formatTimeForAPI = (time24) => {
    if (!time24) return "";
    const [h, m] = time24.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${ampm}`;
};

const normalizeTime = (t) => {
    if (!t) return "";
    if (t.includes("T")) return t.slice(11, 16);
    if (t.includes("AM") || t.includes("PM")) {
        const [tp, m] = t.split(" ");
        let [h, min] = tp.split(":").map(Number);
        if (m === "PM" && h < 12) h += 12;
        if (m === "AM" && h === 12) h = 0;
        return `${h.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`;
    }
    return t.slice(0, 5);
};

const consultationSchema = yup.object().shape({
    title: yup.string().required("Title is required"),
    description: yup.string().required("Purpose/Description is required"),
    consultation_date: yup.date().required("Consultation Date is required"),
    start_time: yup.string().required("Start time is required"),
    end_time: yup.string()
        .required("End time is required")
        .test("is-after-start", "End time must be after start time", function (value) {
            const { start_time } = this.parent;
            return start_time && value > start_time;
        }),
    venue: yup.string().required("Venue/Link is required"),
    attendees: yup.string().required("Participants are required"),
});

export default function PublicConsultationRecord({
    isOpen,
    onClose,
    mode = "add",
    data = null,
    resolutionName,
    legislativeId,
    legislationType,
    refetch,
    refetchEditPublic
}) {

    const [publicFiles, setPublicFiles] = useState([]);
    const [loading, setLoading] = useState(false);

    const isEditMode = mode === "edit" && Boolean(data);

    const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
        resolver: yupResolver(consultationSchema),
        defaultValues: {
            title: "",
            description: "",
            consultation_date: "",
            start_time: "",
            end_time: "",
            venue: "",
            attendees: "",
            venue_type: 1,
        },
    });

    const { mutateAsync: uploadConsultationRecord } = useSubmission(
        ["upload-consultation-record"],
        (api, args) =>
            api.admin.legislation.uploadConsultationRecord(args.id, args.payload)
    );

    const { mutateAsync: updateConsultationRecord } = useSubmission(
        ["update-consultation-record"],
        (api, args) =>
            api.admin.legislation.updateConsultationRecord(args.id, args.payload)
    );

    useEffect(() => {
        if (mode === "add") {
            reset({
                title: "",
                description: "",
                consultation_date: "",
                start_time: "",
                end_time: "",
                venue: "",
                attendees: "",
                venue_type: 1,
            });
            setPublicFiles([]);
        } else if (isEditMode && data) {
            reset({
                title: data.title || "",
                description: data.summary || "",
                consultation_date: data.consultation_date || "",
                start_time: normalizeTime(data.start_time),
                end_time: normalizeTime(data.end_time),
                venue: data.venue || "",
                attendees: data.attendees || "",
                venue_type: Number(data.venue_type) || 1,
            });
            setPublicFiles(data?.public_file || null);
        }
    }, [mode, isEditMode, data, reset]);

    const handleFormSubmit = async (formData) => {
        setLoading(true);

        try {
            const payload = new FormData();

            const formattedDate = new Date(formData.consultation_date)
                .toISOString()
                .slice(0, 10)
                .replace(/-/g, "/");

            payload.append("consultation_date", formattedDate);
            payload.append("title", formData.title);
            payload.append("description", formData.description);
            payload.append("summary", formData.description);
            payload.append("start_time", formatTimeForAPI(formData.start_time));
            payload.append("end_time", formatTimeForAPI(formData.end_time));
            payload.append("venue", formData.venue);
            payload.append("venue_type", formData.venue_type);
            payload.append("attendees", formData.attendees);
            payload.append(
                "attendees_count",
                formData.attendees ? formData.attendees.split(",").length : 0
            );

            if (publicFiles?.type === 'new') {
                payload.append('public_file', publicFiles.file)
            }

            if (publicFiles?.type === 'existing' && publicFiles?.remove === 1) {
                payload.append('remove_public_file', 1)
            }

            if (mode === "add") {
                await uploadConsultationRecord({
                    id: legislativeId,
                    payload,
                });
                toast.success("Consultation record added successfully!");
            } else {
                await updateConsultationRecord({
                    id: data?.id,
                    payload,
                });
                toast.success("Consultation record updated successfully!");
            }

            refetch();
            refetchEditPublic?.();
            onClose();

        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Request failed");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;


    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-r from-[#60A4F9] to-[#2865EC] px-3 py-1 rounded">
                        <h3 className="text-white font-semibold">
                            {legislationType?.charAt(0).toUpperCase() + legislationType?.slice(1)} No. {resolutionName}
                        </h3>
                    </div>
                    <span className="text-md font-semibold text-gray-800">
                        {mode === "add" ? "Add Consultation Record" : "Edit Consultation Record"}
                    </span>
                </div>
            }
            footer={
                <div className="flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="px-5 py-2 border border-gray-300 rounded-lg">
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit(handleFormSubmit)}
                        disabled={loading}
                        className="px-5 py-2 bg-blue-600 text-white rounded-lg"
                    >
                        {loading ? "Saving..." : mode === "add" ? "Create" : "Update"}
                    </button>
                </div>
            }
            size="6xl"
        >
            <form className="px-5 py-5 space-y-6 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input
                            {...register("title")}
                            className={`mt-1 block w-full border rounded-md p-2.5 focus:ring-blue-500 focus:border-blue-500 ${errors.title ? "border-red-500" : "border-gray-300"}`}
                        />
                        {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Purpose/Description</label>
                        <input
                            {...register("description")}
                            className={`mt-1 block w-full border rounded-md p-2.5 focus:ring-blue-500 focus:border-blue-500 ${errors.description ? "border-red-500" : "border-gray-300"}`}
                        />
                        {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Consultation Date</label>
                        <input
                            {...register("consultation_date")}
                            type="date"
                            className={`block w-full border rounded-md p-2.5 focus:ring-blue-500 focus:border-blue-500 ${errors.consultation_date ? "border-red-500" : "border-gray-300"}`}
                        />
                        {errors.consultation_date && <p className="text-red-500 text-sm mt-1">{errors.consultation_date.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Participants</label>
                        <input
                            {...register("attendees")}
                            className={`mt-1 block w-full border rounded-md p-2.5 focus:ring-blue-500 focus:border-blue-500 ${errors.attendees ? "border-red-500" : "border-gray-300"}`}
                            placeholder="Comma-separated names"
                        />
                        {errors.attendees && <p className="text-red-500 text-sm mt-1">{errors.attendees.message}</p>}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div >
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                            <input
                                {...register("start_time")}
                                type="time"
                                className={`block w-full border rounded-md p-2.5 focus:ring-blue-500 focus:border-blue-500 ${errors.start_time ? "border-red-500" : "border-gray-300"}`}
                            />
                            {errors.start_time && <p className="text-red-500 text-sm mt-1">{errors.start_time.message}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                            <input
                                {...register("end_time")}
                                type="time"
                                className={`block w-full border rounded-md p-2.5 focus:ring-blue-500 focus:border-blue-500 ${errors.end_time ? "border-red-500" : "border-gray-300"}`}
                            />
                            {errors.end_time && <p className="text-red-500 text-sm mt-1">{errors.end_time.message}</p>}
                        </div>
                    </div>
                    <div>
                        <Controller
                            name="venue_type"
                            control={control}
                            render={({ field }) => (
                                <div className="flex flex-col">
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="block text-sm font-medium text-gray-700">Venue</label>
                                        <div className="flex items-center gap-6 text-sm">
                                            <label className="flex items-center text-gray-700">
                                                <input
                                                    type="radio"
                                                    checked={field.value === 0}
                                                    onChange={() => field.onChange(0)}
                                                    className="mr-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                />
                                                In Person
                                            </label>
                                            <label className="flex items-center text-gray-700">
                                                <input
                                                    type="radio"
                                                    checked={field.value === 1}
                                                    onChange={() => field.onChange(1)}
                                                    className="mr-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                />
                                                Virtual
                                            </label>
                                        </div>
                                    </div>
                                    <input
                                        {...register("venue")}
                                        placeholder={field.value === 1 ? "Meeting Link" : "Physical Venue"}
                                        className={`block w-full border rounded-md p-2.5 focus:ring-blue-500 focus:border-blue-500 ${errors.venue ? "border-red-500" : "border-gray-300"}`}
                                    />
                                    {errors.venue && <p className="text-red-500 text-sm mt-1">{errors.venue.message}</p>}
                                </div>
                            )}
                        />
                    </div>
                </div>
                <FileUploader
                    label="Public Records"
                    inputId={`public-upload-${mode}`}
                    initialFiles={publicFiles ? [publicFiles] : []}
                    onFilesChange={(files) => setPublicFiles(files[0] || null)}
                    multiple={false}
                    mode="edit"
                />
            </form>
        </Modal>
    );
}
